import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { t } from '@lingui/macro';
import DOMPurify from 'dompurify';
import * as H from 'history';
import { Util } from './Util';
import { defaultLanguage } from '../../locales/InitTranslation';
import { UIMessage } from '../../ui/components/UIMessage';
import { SoribotSubType } from '../../models/local/Types';

// let {isActive} = await App.getState();

export class UtilApp {

  static getThreadText(m: UIMessage): string {
    const textReplies = m.children?.filter(c => !!c.msg.text) ?? []
    const thread = [m].concat(textReplies);
    let nr = 0;
    const allText = thread.map(c => {
      const author = c.msg.user?.name ?? `Unknown`;
      const whoSaid = (c.msg.replyTo && c.msg.user?.id != c.msg.replyTo.id) ? t`${author} wrote to ${c.msg.replyTo.name}:` : t`${author} wrote:`;
      const aiCommentText = c.msg.comments?.map(c => `\n\n${c.name}: ${c.text}`).join('');
      nr += 1;
      return `#${nr}. ${whoSaid}\n` + `${c.msg?.text} ${aiCommentText}`;
    }).join('\n\n');
    return Util.getTextAsPlain(allText);
  }

  static dateStrToTimeAndDay(dateString: any, userLang?: string): string {
    let date = new Date(dateString);
    return `${date.toTimeString().substr(0, 5)}, ${this.dateStrToDay(dateString, userLang)}`;
  }

  static dateStrToTime(dateString: any): string {
    let date = new Date(dateString);
    return `${date.toTimeString().substr(0, 5)}`;
  }

  static dateStrToLocaleDateString(dateString: any, userLang?: string) {
    const locale = userLang || navigator.language;
    return new Date(dateString).toLocaleDateString(locale, { hour: '2-digit', minute: '2-digit' })
  }

  static dateStrToDay(dateString: any, userLang?: string): string {
    if (!Boolean(dateString)) {
      return t`(no date)`;
    }
    let date = new Date(dateString);
    let today = new Date();
    let day = date.toDateString();
    let year = date.getFullYear();
    // 언어 우선순위: userLang → navigator.language
    const locale = userLang || navigator.language;
    if (day == today.toDateString()) {
      return t`today`;
    } else if (day == new Date(today.getTime() + Util.DAY_MILLISECONDS).toDateString()) {
      return t`tomorrow`;
    } else if (year == today.getFullYear()) {
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    } else
      return date.toLocaleDateString(locale);
  }

  static dateStrToYmd(dateString: any, userLang?: string): string {
    if (!Boolean(dateString)) {
      return t`(no date)`;
    }
    let date = new Date(dateString);
    let today = new Date();
    let day = date.toDateString();
    let year = date.getFullYear();
    // 언어 우선순위: userLang → navigator.language
    const locale = userLang || navigator.language;
    if (year == today.getFullYear()) {
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    } else
      return date.toLocaleDateString(locale);
  }

  static sanitizedAndLinkifiedHTML(rawHtml: string | undefined): string {
    if (rawHtml) {
      var clean = DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS: ['span', 'img', 'strong'] });
      let regexp = /(http|https)\:\/\/(\S*[^\W])/g;
      return clean.replace(regexp, '<a href="$1://$2" target="_blank" rel="nofollow">$1://$2</a>');
    } else {
      return "";
    }
  }

  static sanitizedHTML(rawHtml: string | undefined): string {
    if (rawHtml) {
      return DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS: ['br'] });
    } else {
      return "";
    }
  }

  static getPathFromURL(url: string | undefined) {
    return new URL(url ?? "").pathname;
  }

  static getParam(name: string): string | null {
    return new URLSearchParams(window.location.search).get(name);
  }

  static getLastPathSegment() {
    return window.location.pathname.split('/').pop() ?? "";
  }

  static getRelativeDate(daysFromNow: number) {
    return new Date(new Date().getTime() + daysFromNow * Util.DAY_MILLISECONDS);
  }

  static isMac(window: Window) {
    return !Capacitor.isNativePlatform() && window.navigator.userAgent.toLowerCase().includes('mac os x')
  }

  static getPlatform(): 'ios-browser' | 'android-browser' | 'ios-native' | 'android-native' | 'web' | 'webview' {
    const ua = navigator.userAgent
    if (Capacitor.isNativePlatform()) {
      if (Capacitor.getPlatform() == 'ios')
        return 'ios-native';
      else if (Capacitor.getPlatform() == 'android')
        return 'android-native'
      else
        return 'webview'
    } else if (/android/i.test(ua)) {
      return "android-browser"
    } else if (/iPad|iPhone|iPod/.test(ua)) {
      return "ios-browser"
    }
    return 'web';
  }

  static isIPadApp(): boolean {
    const ua = navigator.userAgent;
    return Capacitor.isNativePlatform() && /iPad|Macintosh/.test(ua); // Macintosh for simulator
  }

  static isSafariBrowser(): boolean {
    // True if Safari browser runs on iOS or Desktop, not as a webview.
    if (typeof navigator === 'undefined')
      return false;

    return !Capacitor.isNativePlatform() && !navigator.userAgent.includes('Chrome') && navigator.userAgent.includes('Safari');
  }

  static isChromeBrowser(): boolean {
    return !Capacitor.isNativePlatform() && navigator.userAgent.includes('Chrome');
  }

  static isMobileSize(): boolean {
    return window.innerWidth < 768;
  }

  static canOnlyPlayMP4(): boolean {
    // safari mobile or safari webview on iOS can only play mp4.
    const isSafarWebView = this.getPlatform() == 'ios-native';
    const isSafariOnMobile = /iP(ad|od|hone)/i.test(window.navigator.userAgent) && !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);
    return isSafarWebView || isSafariOnMobile;
  }

  static nrMinAgo(created: Date) {
    let now = new Date();
    let diff = now.getTime() - new Date(created).getTime();
    let minutes = Math.floor(diff / 60000);
    return minutes;
  }

  static async isForeground() {
    if (Capacitor.isNativePlatform()) {
      let r = await App.getState();
      return r.isActive;
    } else {
      return document.hasFocus();
    }
  }

  static async isElementVisible(domElement: HTMLElement | null): Promise<boolean> {
    if (!domElement) {
      return false;
    }

    return new Promise(resolve => {
      const o = new IntersectionObserver(([entry]) => {
        resolve(entry.intersectionRatio === 1);
        o.disconnect();
      });
      o.observe(domElement!);
    });
  }

  static isWorkAppWebView() {
    return navigator.userAgent.includes("WorkWebView");
  }

  static runOnEnter(e: React.KeyboardEvent<any>, handler: () => void) {
    if (e.key == 'Enter') {
      e.preventDefault();
      handler();
    }
  }

  static getTextAsHTML(messageText: string | undefined, userId: string) {
    if (messageText) {
      if (messageText.length < 5000) { // Will not apply to Dalle images <img src="data:image/jpeg;base64....
        messageText = messageText.replaceAll('<', '&lt;')
      }
      messageText = messageText.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      if (userId) {
        // 새 형식: [이름](user:id), 기존 형식: @[이름](user:id) 모두 지원
        messageText = messageText.replace(/@?\[(.+?)\]\(user:(.*?)\)/g, (match, name, id) => {
          let isMe = id === userId;
          let displayName = name;

          // everyone 멘션인 경우 현재 언어로 번역
          if (id === 'everyone') {
            displayName = t`Everyone`;
          }

          return `<span class="mention ${isMe ? 'is-me' : 'is-other'}">@${displayName}</span>`;
        })
      }
      return UtilApp.sanitizedAndLinkifiedHTML(messageText);
    }
    return "";
  }

  static getLanguageName(lang: string | undefined): string {
    if (!lang) {
      lang = defaultLanguage;
    }
    switch (lang) {
      case 'ko':
        return t`Korean`;
      case 'ja':
        return t`Japanese`;
      case 'en':
        return t`English`;
      default:
        return t`English`;
    }
  }

  static getCurrentLanguage() {
    return this.getLanguageName(defaultLanguage);
  }

  static getSoribotSubType(subtype: SoribotSubType | undefined) {
    if (!subtype || subtype === 'retrieval')
      return t`Knowledge Retrieval`;
    else if (subtype === 'general')
      return t`General Bot`;
    else if (subtype === 'broker')
      return t`Broker Bot`;
    else
      return t`Unknown`;
  }

}


// function shouldShowPopup(): boolean {
//   let lastSeenPopup = LocalStorage.get('download-popup-seen');
//   if (lastSeenPopup) {
//     let past = new Date(lastSeenPopup).getTime();
//     let now = new Date().getTime();
//     console.log("popup-past", past, "popup-now", now, (now-past))
//     if (now - past > 60000 ) {
//       return true;
//     }
//   }
//   return (!hasAlreadySeenPopup && !hasAlreadySeenBanner) || (!hasAlreadySeenPopup);
// }

// function shouldShowBanner(): boolean {
//   let lastSeenBanner = LocalStorage.get('download-banner-seen');
//   if (lastSeenBanner) {
//     let past = new Date(lastSeenBanner).getTime();
//     let now = new Date().getTime();
//     console.log("banner-past", past, "banner-now", now, (now-past))
//     if (now - past > 60000) {
//       return true;
//     }
//   }
//   return !!hasAlreadySeenPopup && !hasAlreadySeenBanner;
// }

